/**
 * Показ прогресса: знаменатель и ось «отчитались».
 *
 * Компоненты общие, потому что экранов теперь два — карантин и кампании — и
 * полоса на них обязана означать одно и то же. Разные полосы для одного и
 * того же числа читались бы как разные числа.
 *
 * Что здесь зафиксировано формой, а не договорённостью:
 *
 * * **система учётная.** Внесённый отчёт принимается фактом по умолчанию;
 *   проверка — необязательная отметка поверх, без своей доли и своей полосы
 *   (журнал решений №28, инвариант 11 в `docs/rules/invariants.md`);
 * * **пустой знаменатель не рисуется нулём.** Вместо заливки идёт штриховка
 *   «считать не от чего»: серая пустая полоса читалась бы как ноль процентов;
 * * **неизвестная площадь названа вслух.** Единицы земли без строки площади
 *   показываются отдельной пометкой, а не растворяются в сумме.
 */

import { Area, Badge } from "./ui";
import { StatusLegend, StatusMark } from "./StatusMark";
import styles from "./progress.module.css";
import { areaView, share, type ProgressView, type Share } from "../lib/progress";
import type { ProgressArea, ProgressCount } from "../api/client";
import { formatCount, missingLabel, plural } from "../lib/format";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import type { CountMessage } from "../astra/lib/format";

/** Существительное счёта: русские формы или сообщение-счёт без `#`. */
export type CountNoun = [string, string, string] | CountMessage;

const m = defineMessages({
  noDenominator: { id: "common.kit.progress.noDenominator", defaultMessage: "Считать не от чего: в этом знаменателе нет ни одной единицы", description: "Подсказка к пустой штриховой полосе прогресса: знаменатель равен нулю, процент считать не от чего" },
  verified: { id: "common.kit.progress.verified", defaultMessage: "проверено: {count}", description: "Мелкая строка под полосой прогресса: сколько единиц проверено инспектором; {count} — число" },
  hectares: { id: "common.kit.progress.hectares", defaultMessage: "Гектары", description: "Подпись полосы прогресса по площади (отчитавшаяся площадь из общей)" },
  missingArea: { id: "common.kit.progress.missingArea", defaultMessage: "без площади: {count}", description: "Бейдж под полосой по площади: сколько единиц земли без площади в реестре; коротко" },
  missingAreaHint: { id: "common.kit.progress.missingAreaHint", defaultMessage: "Единицы земли без строки площади в реестре", description: "Подсказка к бейджу «без площади»" },
  overlap: { id: "common.kit.progress.overlap", defaultMessage: "учтено один раз: {count}", description: "Бейдж под полосой по площади: сколько контуров заявлены и целиком, и частями, но посчитаны один раз; коротко" },
  overlapHint: { id: "common.kit.progress.overlapHint", defaultMessage: "Контур заявлен и целиком, и частями. Гектары посчитаны один раз — часть свёрнута в свой контур; сам конфликт реестра требует разбора", description: "Подсказка к бейджу «учтено один раз» (менеджер, аудитор)" },
  noWorks: { id: "common.kit.progress.noWorks", defaultMessage: "работ не назначали", description: "Бейдж в ячейке прогресса: по этой единице работ не назначено вовсе (не «0 %»); коротко" },
  reportedName: { id: "common.kit.progress.reportedName", defaultMessage: "отчитались", description: "Название оси прогресса: отчёт поступил; строчная, над полосой" },
  reportedOf: { id: "common.kit.progress.reportedOf", defaultMessage: "{reported} из {total}", description: "Значение оси «отчитались»: {reported} из {total} — числа, уже отформатированные" },
  verifiedHint: { id: "common.kit.progress.verifiedHint", defaultMessage: "Отметка проверяющего показана счётом, без доли и без отдельной полосы (журнал решений №28).", description: "Подсказка к строке «проверено» под полосой прогресса" },
  withoutReport: { id: "common.kit.progress.withoutReport", defaultMessage: "без отчёта: {count} {noun}", description: "Строка под полосой прогресса: сколько единиц без отчёта; {count} — число, {noun} — что считаем в нужной форме («работ на полях»)" },
  withoutReportHint: { id: "common.kit.progress.withoutReportHint", defaultMessage: "Отчёта по этим единицам не поступало. Это отсутствие данных, а не подтверждённое невыполнение.", description: "Подсказка к строке «без отчёта»: это отсутствие данных, а не вина исполнителя" },
});

/**
 * Дорожка оси. Тон берётся из семантической палитры данных (ADR-0009) и
 * означает ровно одно: какая это ось. Ось «без отчёта» физически не может
 * получить красный — у неё нейтральная ступень и штриховка «данных нет».
 */
export type BarTone = "reported" | "verified" | "missing" | "water";

const BAR_TONE: Record<BarTone, string> = {
  reported: styles.barFillReported ?? "",
  verified: styles.barFillVerified ?? "",
  missing: styles.barFillMissing ?? "",
  water: styles.barFillWater ?? "",
};

export function Bar({
  value,
  verified = false,
  tone,
}: {
  value: Share;
  /** Историческая форма записи `tone="verified"`. */
  verified?: boolean;
  tone?: BarTone;
}) {
  const t = useT();
  const kind: BarTone = tone ?? (verified ? "verified" : "reported");
  if (value.value === null) {
    return (
      <div
        className={`${styles.bar} ${styles.barMissing}`}
        title={t(m.noDenominator)}
      />
    );
  }
  return (
    <div className={styles.bar}>
      <div
        className={`${styles.barFill} ${BAR_TONE[kind]}`}
        style={{ width: `${Math.max(0, Math.min(100, value.value * 100))}%` }}
      />
    </div>
  );
}

export function ProgressRow({ view }: { view: ProgressView }) {
  const t = useT();
  return (
    <div className={styles.progressRow}>
      <div className={styles.progressLabel}>
        <span>{view.label}</span>
        <span className={styles.progressValue}>
          {formatCount(view.reported).text} / {formatCount(view.total).text}
          {view.reportedShare.percent === null
            ? ""
            : ` · ${view.reportedShare.percent}%`}
        </span>
      </div>
      <Bar value={view.reportedShare} />
      <div className={styles.progressValue}>
        {t(m.verified, { count: formatCount(view.verified).text })}
      </div>
    </div>
  );
}

export function AreaProgress({
  area,
  rounded = false,
}: {
  area: ProgressArea;
  /**
   * Округлять до сотых. Нужно там, где полоса стоит в сводке уровня: точное
   * значение в микрогектарах занимает две строки и вытесняет то, ради чего
   * сводку читают. Само точное число остаётся в подсказке и в таблицах ниже.
   */
  rounded?: boolean;
}) {
  const view = areaView(area);
  const t = useT();
  return (
    <div className={styles.progressRow}>
      <div className={styles.progressLabel}>
        <span>{t(m.hectares)}</span>
        <span className={styles.progressValue}>
          <Area microha={view.reportedMicroha} unit={null} rounded={rounded} /> /{" "}
          <Area microha={view.totalMicroha} rounded={rounded} />
          {view.reportedShare.percent === null
            ? ""
            : ` · ${view.reportedShare.percent}%`}
        </span>
      </div>
      <Bar value={view.reportedShare} />
      {view.missingUnits > 0 || view.overlappingUnits > 0 ? (
        <div className={styles.progressValue}>
          {view.missingUnits > 0 ? (
            <Badge
              tone="attention"
              title={t(m.missingAreaHint)}
            >
              {t(m.missingArea, { count: view.missingUnits })}
            </Badge>
          ) : null}
          {view.overlappingUnits > 0 ? (
            <Badge
              tone="attention"
              title={t(m.overlapHint)}
            >
              {t(m.overlap, { count: view.overlappingUnits })}
            </Badge>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * «Что сделано, а что нет» на любом уровне — ОДНИМ компонентом.
 *
 * Он существует ради требования, которое иначе нарушается на каждом новом
 * экране по отдельности: развилка обязана читаться мгновенно и обязана
 * рисовать ОДНУ ось — «отчитались». Отчёт принят как факт; остаток —
 * «без отчёта» — называет отсутствием данных, а не невыполнением. Отметка
 * проверяющего показана рядом счётом, без своей доли и своей полосы
 * (журнал решений №28).
 *
 * Три исхода выглядят по-разному, потому что означают разное:
 *
 * * `counts === null` — считать нечем: работ этого рода в корпусе нет вовсе;
 * * `total === 0` — работ здесь не назначали: это не «ноль процентов»,
 *   а отсутствие предмета счёта;
 * * `total > 0` — доля «отчитались» и остаток.
 */
export function DoneSplit({
  counts,
  noun,
  absent,
  size = "cell",
}: {
  counts: ProgressCount | null | undefined;
  /** В чём считаем: обязательства, контуры, хозяйства. */
  noun: CountNoun;
  /** Почему счёта нет. Обязательно, когда `counts` может отсутствовать. */
  absent?: string;
  /** `cell` — ячейка списка, `hero` — ответ уровня. */
  size?: "cell" | "hero";
}) {
  const t = useT();
  if (!counts) {
    return (
      <div className={styles.splitAbsent}>
        <Badge tone="unknown">{missingLabel()}</Badge>
        {absent ? <span className={styles.splitReason}>{absent}</span> : null}
      </div>
    );
  }
  if (counts.total <= 0) {
    return (
      <div className={styles.splitAbsent}>
        <Badge tone="unknown">{t(m.noWorks)}</Badge>
        {absent ? <span className={styles.splitReason}>{absent}</span> : null}
      </div>
    );
  }

  const reported = share(counts.reported, counts.total);
  const withoutReport = counts.total - counts.reported;

  return (
    <div className={size === "hero" ? styles.splitHero : styles.split}>
      <div className={styles.splitAxis}>
        <span className={styles.splitName}>{t(m.reportedName)}</span>
        <span className={styles.splitValue}>
          {t(m.reportedOf, { reported: formatCount(counts.reported).text, total: formatCount(counts.total).text })}
          {reported.percent === null ? "" : ` · ${reported.percent}%`}
        </span>
      </div>
      <Bar value={reported} />
      <p className={styles.splitReason} title={t(m.verifiedHint)}>
        {t(m.verified, { count: formatCount(counts.verified).text })}
      </p>
      {/* Остаток называется отсутствием отчёта, а не невыполнением: отчёта не
          поступало — это про данные, а не про работу в поле. Поэтому у оси
          нейтральный тон и штриховка «данных нет», а не красная заливка:
          красный в этом продукте означает критичное состояние объекта. */}
      <p
        className={styles.splitRest}
        title={t(m.withoutReportHint)}
      >
        {t(m.withoutReport, { count: formatCount(withoutReport).text, noun: plural(withoutReport, noun) })}
      </p>
      <Bar value={share(withoutReport, counts.total)} tone="missing" />
    </div>
  );
}

/**
 * Легенда трёх отметок статуса. Цвет — не единственный носитель смысла: рядом
 * с каждой дорожкой стоят иконка и подпись того, что отметка УТВЕРЖДАЕТ.
 * Строка «без отчёта» названа отсутствием данных прямо в легенде: без неё
 * она читается как невыполнение на каждом экране заново.
 *
 * Язык здесь ОДИН. Раньше над отметками стояла ещё строка образцов, которая
 * теми же словами называла те же три оси («отчитались — заявление
 * исполнителя» над «отчитались — Заявление исполнителя: отчёт поступил»), и
 * этот повтор ехал на восемь экранов сразу. Оставлена точная форма: иконка
 * плюс подпись, потому что образец цвета без иконки не переживает ни печать,
 * ни дальтонизм. Правила `.axisSample*` убраны вместе со строкой: показывать
 * их стало некому.
 */
export function AxisLegend() {
  return <StatusLegend statuses={["ok", "verified", "missing"]} withMeaning />;
}

/** Отметка оси для таблиц и списков: та же иконка, тот же тон, та же подпись. */
export { StatusMark };

export const progressStyles = styles;
